export interface MarketingContent {
  badge: string
  title: string
  subtitle: string
  ctaLabel: string
  sections: Array<{
    title: string
    description: string
    bullets: string[]
  }>
  faqTitle: string
  faqs: Array<{ question: string; answer: string }>
}

export const enContent: Record<string, MarketingContent> = {
  home: {
    badge: 'Smart portfolio management',
    title: 'Track your full net worth and portfolio performance in one place.',
    subtitle:
      'WealthScale helps you aggregate multi-broker holdings, measure time-weighted returns, and monitor allocations with decision-ready dashboards.',
    ctaLabel: 'Create free account',
    sections: [
      {
        title: 'Portfolio performance',
        description: 'Measure your real investment performance over time.',
        bullets: [
          'Time-weighted return (TWR) calculation',
          'Benchmark comparison against major indices',
          'Cash-flow aware performance tracking',
        ],
      },
      {
        title: 'Allocation intelligence',
        description: 'Understand your risk concentration at a glance.',
        bullets: [
          'Breakdown by asset class, sector, industry and country',
          'Currency exposure monitoring',
          'Portfolio diversification visuals',
        ],
      },
      {
        title: 'Watchlists and screener',
        description: 'Discover opportunities before you invest.',
        bullets: [
          'Build thematic watchlists',
          'Inspect fundamentals and key metrics',
          'Follow market events and news context',
        ],
      },
      {
        title: 'Automated import',
        description: 'Stop managing spreadsheets manually.',
        bullets: [
          'Broker and CSV transaction import',
          'Duplicate detection and validation',
          'Unified transaction ledger',
        ],
      },
    ],
    faqTitle: 'Frequently asked questions',
    faqs: [
      {
        question: 'Is WealthScale free to start?',
        answer: 'Yes. You can create an account and start tracking your portfolio for free.',
      },
      {
        question: 'Can I track multiple brokers?',
        answer: 'Yes. WealthScale is designed to consolidate investments from multiple sources.',
      },
    ],
  },
  features: {
    badge: 'Features',
    title: 'Portfolio tracking features built for long-term investors.',
    subtitle:
      'Get one unified interface for valuation, performance, allocation analysis, transaction history, and market monitoring.',
    ctaLabel: 'Start tracking now',
    sections: [
      {
        title: 'Dashboard',
        description: 'Live view of your assets, value and return.',
        bullets: ['Total wealth timeline', 'Portfolio snapshots', 'Performance cards'],
      },
      {
        title: 'Transactions',
        description: 'Complete trade and cash-flow history.',
        bullets: ['Buy/sell records', 'CSV export', 'Edit and reconciliation tools'],
      },
      {
        title: 'Calendar',
        description: 'Never miss key portfolio events.',
        bullets: ['Dividend events', 'Earnings dates', 'Upcoming reminders'],
      },
      {
        title: 'Screener',
        description: 'Research assets before adding them.',
        bullets: ['Financial statements', 'Fundamental metrics', 'Interactive charts'],
      },
    ],
    faqTitle: 'Feature FAQ',
    faqs: [
      {
        question: 'Do you support benchmark comparison?',
        answer: 'Yes. You can compare portfolio return against benchmark indices.',
      },
      {
        question: 'Can I manage several portfolios?',
        answer: 'Yes. WealthScale supports multiple portfolios in one account.',
      },
    ],
  },
  useCases: {
    badge: 'Use cases',
    title: 'Built for investors who need clarity across brokers and asset classes.',
    subtitle:
      'Whether you are tracking stocks, ETFs, crypto or cash, WealthScale helps you make allocation and performance decisions faster.',
    ctaLabel: 'Open WealthScale',
    sections: [
      {
        title: 'Long-term investors',
        description: 'Measure compounding with confidence.',
        bullets: ['TWR based performance', 'Benchmark context', 'Allocation drift visibility'],
      },
      {
        title: 'Multi-broker users',
        description: 'Get one source of truth across accounts.',
        bullets: ['Aggregated holdings', 'Unified transaction feed', 'Cross-account insights'],
      },
      {
        title: 'Dividend-focused portfolios',
        description: 'Track expected and paid income.',
        bullets: ['Dividend calendar', 'Income history', 'Event visibility'],
      },
      {
        title: 'Active researchers',
        description: 'Find ideas and monitor signals.',
        bullets: ['Watchlist workflows', 'Screener metrics', 'News and events tabs'],
      },
    ],
    faqTitle: 'Use-case FAQ',
    faqs: [
      {
        question: 'Can I track ETFs and stocks together?',
        answer: 'Yes. WealthScale supports mixed portfolios with unified reporting.',
      },
      {
        question: 'Is WealthScale suitable for international investors?',
        answer: 'Yes. WealthScale includes multi-currency monitoring and conversion handling.',
      },
    ],
  },
  faq: {
    badge: 'Help center',
    title: 'Answers to the most common WealthScale questions.',
    subtitle:
      'Understand how tracking, imports, performance metrics, and security work before creating your account.',
    ctaLabel: 'Sign up free',
    sections: [
      {
        title: 'Getting started',
        description: 'Setup steps to track your first portfolio quickly.',
        bullets: ['Create account', 'Import transactions', 'Review allocations'],
      },
      {
        title: 'Performance metrics',
        description: 'How WealthScale calculates returns.',
        bullets: ['TWR methodology', 'Benchmark comparison', 'Cash-flow neutrality'],
      },
      {
        title: 'Data and imports',
        description: 'Import and data quality behavior.',
        bullets: ['CSV import support', 'Validation checks', 'Duplicate detection'],
      },
      {
        title: 'Security',
        description: 'Account and session protection.',
        bullets: ['WorkOS authentication', 'Token-based access', 'Production deployment'],
      },
    ],
    faqTitle: 'General FAQ',
    faqs: [
      {
        question: 'Do I need to manually enter every trade?',
        answer: 'No. You can import transactions from supported broker formats.',
      },
      {
        question: 'Can I access WealthScale on mobile?',
        answer: 'Yes. The web app is responsive and works on mobile browsers.',
      },
    ],
  },
  contact: {
    badge: 'Contact',
    title: 'Talk to the WealthScale team.',
    subtitle:
      'Questions about portfolio tracking, feature support, or product roadmap? Reach us and we will get back to you.',
    ctaLabel: 'Create account',
    sections: [
      {
        title: 'Product questions',
        description: 'Understand what fits your workflow.',
        bullets: ['Feature walkthrough', 'Supported use cases', 'Roadmap highlights'],
      },
      {
        title: 'Technical support',
        description: 'Need help with setup or imports.',
        bullets: ['Import troubleshooting', 'Data quality checks', 'Issue triage'],
      },
      {
        title: 'Partnerships',
        description: 'Discuss integrations and collaboration.',
        bullets: ['Broker data workflows', 'Technical architecture', 'API roadmap'],
      },
      {
        title: 'Reach us',
        description: 'Primary contact channel.',
        bullets: ['Email: contact@wealthscale.xyz', 'Response in business days', 'English and French'],
      },
    ],
    faqTitle: 'Contact FAQ',
    faqs: [
      {
        question: 'How can I contact WealthScale?',
        answer: 'Use contact@wealthscale.xyz for product and support requests.',
      },
      {
        question: 'Do you answer in French?',
        answer: 'Yes, we support both English and French conversations.',
      },
    ],
  },
}

export const frContent: Record<string, MarketingContent> = {
  home: {
    badge: 'Pilotage de portefeuille',
    title: 'Suivez votre patrimoine et la performance de vos investissements au même endroit.',
    subtitle:
      'WealthScale consolide vos comptes multi-courtiers, calcule vos performances TWR et vous aide à piloter vos allocations.',
    ctaLabel: 'Créer un compte gratuit',
    sections: [
      {
        title: 'Performance portefeuille',
        description: 'Mesurez la vraie performance de vos investissements.',
        bullets: [
          'Calcul Time-Weighted Return (TWR)',
          'Comparaison à des indices de référence',
          'Suivi robuste malgré les flux de trésorerie',
        ],
      },
      {
        title: 'Analyse d’allocation',
        description: 'Visualisez vos concentrations de risque.',
        bullets: [
          'Répartition par classe d’actifs, secteur, industrie, pays',
          'Suivi de l’exposition devises',
          'Vue claire de la diversification',
        ],
      },
      {
        title: 'Watchlists et screener',
        description: 'Repérez des opportunités avant d’investir.',
        bullets: [
          'Watchlists thématiques',
          'Métriques fondamentales',
          'Actualités et événements de marché',
        ],
      },
      {
        title: 'Import automatisé',
        description: 'Fin des tableurs manuels.',
        bullets: [
          'Import de transactions via brokers/CSV',
          'Détection de doublons',
          'Historique consolidé',
        ],
      },
    ],
    faqTitle: 'Questions fréquentes',
    faqs: [
      {
        question: 'WealthScale est-il gratuit pour commencer ?',
        answer: 'Oui, vous pouvez créer un compte et commencer gratuitement.',
      },
      {
        question: 'Puis-je suivre plusieurs courtiers ?',
        answer: 'Oui, WealthScale est conçu pour agréger plusieurs sources.',
      },
    ],
  },
  features: {
    badge: 'Fonctionnalités',
    title: 'Des fonctionnalités de suivi portefeuille pour investisseurs exigeants.',
    subtitle:
      'Retrouvez dans une seule interface la valorisation, la performance, les allocations, les transactions et le suivi marché.',
    ctaLabel: 'Commencer maintenant',
    sections: [
      {
        title: 'Dashboard',
        description: 'Vue d’ensemble de vos actifs et performances.',
        bullets: ['Évolution du patrimoine', 'Synthèse portefeuille', 'Indicateurs clés'],
      },
      {
        title: 'Transactions',
        description: 'Historique complet de vos mouvements.',
        bullets: ['Achats/ventes', 'Export CSV', 'Édition et correction'],
      },
      {
        title: 'Calendrier',
        description: 'Suivez les événements financiers importants.',
        bullets: ['Dividendes', 'Publications de résultats', 'Échéances à venir'],
      },
      {
        title: 'Screener',
        description: 'Analysez les actifs avant décision.',
        bullets: ['États financiers', 'Ratios fondamentaux', 'Graphiques interactifs'],
      },
    ],
    faqTitle: 'FAQ fonctionnalités',
    faqs: [
      {
        question: 'Peut-on comparer la performance à un benchmark ?',
        answer: 'Oui, WealthScale permet la comparaison à des indices.',
      },
      {
        question: 'Puis-je gérer plusieurs portefeuilles ?',
        answer: 'Oui, plusieurs portefeuilles sont pris en charge.',
      },
    ],
  },
  useCases: {
    badge: 'Cas d’usage',
    title: 'Pensé pour les investisseurs multi-courtiers et multi-actifs.',
    subtitle:
      'Actions, ETF, crypto et cash: WealthScale centralise vos données pour améliorer vos décisions d’allocation.',
    ctaLabel: 'Découvrir WealthScale',
    sections: [
      {
        title: 'Investisseur long terme',
        description: 'Suivez votre performance dans la durée.',
        bullets: ['Mesure TWR', 'Contexte benchmark', 'Dérive d’allocation'],
      },
      {
        title: 'Multi-courtiers',
        description: 'Unifier des comptes dispersés.',
        bullets: ['Positions consolidées', 'Flux unifiés', 'Vue globale'],
      },
      {
        title: 'Portefeuille dividendes',
        description: 'Pilotez vos revenus futurs.',
        bullets: ['Calendrier dividendes', 'Historique de revenus', 'Événements à venir'],
      },
      {
        title: 'Analyse active',
        description: 'Identifier de nouvelles opportunités.',
        bullets: ['Watchlists', 'Métriques screener', 'News et événements'],
      },
    ],
    faqTitle: 'FAQ cas d’usage',
    faqs: [
      {
        question: 'Puis-je mélanger ETF et actions ?',
        answer: 'Oui, WealthScale gère des portefeuilles multi-classes d’actifs.',
      },
      {
        question: 'L’application gère-t-elle plusieurs devises ?',
        answer: 'Oui, le suivi multi-devises est intégré.',
      },
    ],
  },
  faq: {
    badge: 'Aide',
    title: 'Les réponses aux questions les plus fréquentes sur WealthScale.',
    subtitle:
      'Comprenez le fonctionnement des imports, du suivi de performance et de la sécurité avant de créer votre compte.',
    ctaLabel: 'Créer un compte',
    sections: [
      {
        title: 'Démarrage',
        description: 'Mise en place rapide de votre suivi.',
        bullets: ['Création de compte', 'Import des transactions', 'Validation des allocations'],
      },
      {
        title: 'Indicateurs de performance',
        description: 'Méthodologie de calcul.',
        bullets: ['TWR', 'Comparaison benchmark', 'Neutralité des flux'],
      },
      {
        title: 'Données et imports',
        description: 'Qualité et contrôle des données.',
        bullets: ['Support CSV', 'Validation des formats', 'Détection de doublons'],
      },
      {
        title: 'Sécurité',
        description: 'Protection des accès.',
        bullets: ['Authentification WorkOS', 'Gestion des sessions', 'Déploiement production'],
      },
    ],
    faqTitle: 'FAQ générale',
    faqs: [
      {
        question: 'Dois-je saisir toutes mes transactions à la main ?',
        answer: 'Non, vous pouvez importer vos transactions via formats supportés.',
      },
      {
        question: 'WealthScale fonctionne-t-il sur mobile ?',
        answer: 'Oui, l’application est responsive.',
      },
    ],
  },
  contact: {
    badge: 'Contact',
    title: 'Échangez avec l’équipe WealthScale.',
    subtitle:
      'Des questions sur les fonctionnalités, le support ou la roadmap produit ? Nous vous répondons par e-mail.',
    ctaLabel: 'Créer un compte',
    sections: [
      {
        title: 'Questions produit',
        description: 'Vérifier l’adéquation à votre besoin.',
        bullets: ['Parcours fonctionnalités', 'Cas d’usage couverts', 'Vision roadmap'],
      },
      {
        title: 'Support technique',
        description: 'Besoin d’aide sur l’import ou la configuration.',
        bullets: ['Aide import', 'Contrôle qualité données', 'Résolution d’incident'],
      },
      {
        title: 'Partenariats',
        description: 'Discuter intégrations et collaboration.',
        bullets: ['Flux de données brokers', 'Architecture technique', 'Évolutions API'],
      },
      {
        title: 'Nous contacter',
        description: 'Canal principal.',
        bullets: ['Email: contact@wealthscale.xyz', 'Réponse en jours ouvrés', 'Français et anglais'],
      },
    ],
    faqTitle: 'FAQ contact',
    faqs: [
      {
        question: 'Comment contacter WealthScale ?',
        answer: 'Écrivez à contact@wealthscale.xyz.',
      },
      {
        question: 'Puis-je échanger en français ?',
        answer: 'Oui, nous répondons en français et en anglais.',
      },
    ],
  },
}
